import type { useToast } from "@chakra-ui/react";
import { scheduleInputToUtcIso } from "@common/utils/adsetSchedule";
import type { GuidedCreateCampaignParams } from "../guidedFlow/types";
import actions from "src/actions";
import { guidedEditWiredBudgetToLaunchCampaignWire } from "./guidedCampaignBudgetWire";
import { wireGuidedEditBudgetFromFormDollars } from "./wireGuidedEditBudgetFromForm";

export type LaunchCampaignParams = GuidedCreateCampaignParams;
export type LaunchCampaignResult = {
  campaignId: string;
  publishUpdateId?: string | null;
  publishAttempted?: boolean;
};

export function buildLaunchCampaignPayload(params: LaunchCampaignParams) {
  const wired = wireGuidedEditBudgetFromFormDollars(
    params.budget ?? {},
    params.eventTimezone
  );
  const budget = guidedEditWiredBudgetToLaunchCampaignWire(wired);
  const apiCreatives = params.creatives.map((c) => ({
    name: c.name ?? "",
    primary_text: c.primaryText.trim(),
    ...(c.squareMediaUrl ? { square_url: c.squareMediaUrl } : {}),
    ...(c.squareMediaFileKey ? { square_file_key: c.squareMediaFileKey } : {}),
    ...(c.squareMediaType ? { square_media_type: c.squareMediaType } : {}),
    ...(c.verticalMediaUrl ? { portrait_url: c.verticalMediaUrl } : {}),
    ...(c.verticalMediaFileKey
      ? { portrait_file_key: c.verticalMediaFileKey }
      : {}),
    ...(c.verticalMediaType
      ? { portrait_media_type: c.verticalMediaType }
      : {}),
    fb_page_id: c.fbPage?.id ?? "",
    fb_page_name: c.fbPage?.name ?? "",
    instagram_id: c.instagramAccount?.id ?? "",
    instagram_name: c.instagramAccount?.username ?? "",
  }));
  return {
    event_id: params.event_id,
    ad_account_id: params.ad_account_id,
    budget,
    schedule: {
      start_at: scheduleInputToUtcIso(
        params.schedule.start_at,
        params.eventTimezone
      )!,
      end_at: scheduleInputToUtcIso(
        params.schedule.end_at,
        params.eventTimezone
      )!,
    },
    creatives: apiCreatives,
    targeting: {
      strategy: params.targeting?.strategy ?? "template",
      ...(params.targeting?.custom_audiences?.length
        ? { custom_audiences: params.targeting.custom_audiences }
        : {}),
      ...(params.targeting?.locations
        ? { locations: params.targeting.locations }
        : {}),
      ...(params.targeting?.demographics
        ? { demographics: params.targeting.demographics }
        : {}),
      ...(params.targeting?.enabled_strategy_ids?.length
        ? { enabled_strategy_ids: params.targeting.enabled_strategy_ids }
        : {}),
    },
  };
}

export async function launchCampaignWithPolling(
  params: LaunchCampaignParams,
  deps: {
    toast: ReturnType<typeof useToast>;
    pollForResult: (args: {
      campaign_id: string;
      update_id: string;
      title: string;
      description: string;
      onStatusUpdate?: (status: string | null) => void;
      onDone?: (success: boolean) => void;
    }) => Promise<void>;
    onStatusUpdate?: (status: string | null) => void;
    onDone?: (success: boolean) => void;
    awaitPolling?: boolean;
  }
) {
  const payload = buildLaunchCampaignPayload(params);
  const response = (await actions.ADMIN.launchCampaign(payload)) as {
    campaign_id?: string;
    publish?: {
      attempted?: boolean;
      succeeded?: boolean;
      error?: string | null;
      update_id?: string | null;
    };
  };
  if (!response?.campaign_id) {
    return null as LaunchCampaignResult | null;
  }
  const result: LaunchCampaignResult = {
    campaignId: response.campaign_id,
    publishUpdateId: response.publish?.update_id ?? null,
    publishAttempted: Boolean(response.publish?.attempted),
  };
  if (
    response.publish?.attempted &&
    response.publish.succeeded === false
  ) {
    const description =
      response.publish.error?.trim() ||
      "The campaign was saved, but activation did not start.";
    deps.toast({
      title: "Activation did not start",
      description,
      status: "error",
    });
  }
  if (
    response?.publish?.attempted &&
    response?.campaign_id &&
    response?.publish?.update_id &&
    (deps.awaitPolling ?? true)
  ) {
    await deps.pollForResult({
      campaign_id: response.campaign_id,
      update_id: response.publish.update_id,
      title: "Campaign published",
      description: "Campaign published",
      onStatusUpdate: deps.onStatusUpdate,
      onDone: deps.onDone
        ? (success: boolean) => deps.onDone?.(success)
        : undefined,
    });
  }
  return result;
}
